import Form from 'antd/lib/form'
import { FormInstance } from 'antd/lib/form/Form'
import Input from 'antd/lib/input'
import Select from 'antd/lib/select'
import { DifficultyLevel } from 'Constants/tasks.constants'
import { useEffect, useMemo, useState } from 'react'
import classes from './GenerateResponseForm.module.less'
import InputNumber from 'antd/lib/input-number'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import { ActionModal } from 'Components/modal/ActionModal/ActionModal'
import InfoCircleFilled from '@ant-design/icons/lib/icons/InfoCircleFilled'
import { GenerateResponseFormType, PromptAnnotationsType } from 'Types/Tasks.types'
import Flex from 'antd/lib/flex'
import TextArea from 'antd/lib/input/TextArea'
import { ComplexityLevelOptions, InAccurateReasonOptions } from 'Constants/customTask.constants'
import { ModelFeedbackFormField } from '../ModelFeedback/ModelFeedbackFormField'
import { CustomForm } from 'Types/CustomTask.types'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { LiveFeedback } from 'Tasks/CustomTask/UI/common/LiveFeedback/LiveFeedback'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { LiveFeedbackPlugin } from 'Plugins/LiveFeedback/LiveFeedback.plugin'
import { LiveFeedbackContextType, LiveFeedbackPluginConfigType } from 'Types/LiveFeedback.types'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'

type GenerateResponseFormPropsType = {
    form: FormInstance<GenerateResponseFormType>
    onSubmit: () => void
    setForms: (forms: CustomForm) => void
    initialValues?: GenerateResponseFormType
    isReviewMode?: boolean
    isReadOnly?: boolean
    enableTurnAnnotations: boolean
    hasPromptOptions: boolean
    modelResponseExtraInfo: {
        totalModelResponsesOnPromptRevision: number
        modelResponseFeedbackCode: { value: number; label: string }[]
        modelResponse: string
    }
    attributesUI?: UpdatableReactElement
    isLMFProject: boolean
    isStemProject: boolean
    installedPlugins?: IPlugin[]
    question: string
    setIsLiveFeedbackChecked: () => void
    promptAnnotations?: PromptAnnotationsType
    liveFeedbackContext: LiveFeedbackContextType
    areAttributesValid: () => Promise<boolean>
}

export const GenerateResponseForm = (props: GenerateResponseFormPropsType) => {
    const { form, onSubmit, isReviewMode, isReadOnly, setForms } = props
    const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false)

    const feedbackValues = Form.useWatch('modelFeedbackDetails', form)
    const humanFinalAnswer = Form.useWatch('humanCompleteFinalAnswer', form)
    const humanReasoningAnswer = Form.useWatch('humanFinalAnswerReasoning', form)

    useEffect(() => {
        setForms({
            generateResponseForm: form,
            finalSubmissionForm: form,
        })
    }, [form, setForms])

    useEffect(() => {
        if (!props.initialValues && props.modelResponseExtraInfo.modelResponseFeedbackCode) {
            form.setFieldsValue({
                modelFeedbackDetails: props.modelResponseExtraInfo.modelResponseFeedbackCode.map(
                    feedbackCode => ({
                        feedbackResultCode: feedbackCode.value,
                    })
                ),
            })
        }
    }, [form, props.initialValues, props.modelResponseExtraInfo.modelResponseFeedbackCode])

    const initialValues = useMemo(() => {
        const {
            domain,
            sub_domain,
            time_for_strategy,
            time_to_solve,
            difficulty,
            complexity,
            accuracy,
            human_complete_final_answer,
            human_final_answer_reasoning,
        } = props.promptAnnotations || {}
        return {
            domain: domain ?? null,
            subDomain: sub_domain ?? null,
            timeToDevelopStrategy: time_for_strategy ?? null,
            timeToSolve: time_to_solve ?? null,
            difficultyLevel: difficulty ?? DifficultyLevel.Easy,
            complexityLevel: complexity ?? ComplexityLevelOptions[0].value,
            accuracy: accuracy
                ? InAccurateReasonOptions.find(option => option.value === accuracy)?.value
                : InAccurateReasonOptions[0].value,
            humanFinalAnswer: null,
            humanFinalAnswerReasoning: human_final_answer_reasoning ?? null,
            humanCompleteFinalAnswer: human_complete_final_answer ?? null,
            modelReasoningTrace: null,
            modelReasoningTakenFrom: null,
            modelFinalAnswer: null,
            modelFeedbackDetails: [],
        }
    }, [props.promptAnnotations])

    const submitContents = useMemo(() => {
        if (isReviewMode || !props.enableTurnAnnotations) {
            return
        }

        return (
            <>
                <ActionButton
                    color="primary"
                    onClick={form.submit}
                    title="Generate Response"
                    className={classes.generateResponseButton}
                />
                <ActionModal
                    getVisibility={() => isSubmitModalVisible}
                    title={
                        <>
                            <InfoCircleFilled className={classes.infoIcon} />
                            Warning
                        </>
                    }
                    content="Please make sure all the fields are correct, as you can’t change it later."
                    primaryButtonName={'Generate'}
                    onConfirm={onSubmit}
                    onCancel={() => setIsSubmitModalVisible(false)}
                ></ActionModal>
            </>
        )
    }, [isReviewMode, props.enableTurnAnnotations, form.submit, isSubmitModalVisible, onSubmit])

    const options = useMemo(
        () =>
            Array(props.modelResponseExtraInfo.totalModelResponsesOnPromptRevision)
                .fill(0)
                .map((_, index) => ({
                    label: `Response ${index + 1}`,
                    value: `Response ${index + 1}`,
                })),
        [props.modelResponseExtraInfo.totalModelResponsesOnPromptRevision]
    )

    const fieldsForWithoutTurnAnnotationsFlow = useMemo(() => {
        if (props.enableTurnAnnotations) return null
        return (
            <>
                <Form.Item<GenerateResponseFormType>
                    className={classes.inputField}
                    rules={[
                        {
                            required: true,
                            message: 'Please enter the correct final human answer',
                        },
                    ]}
                    name={'humanCompleteFinalAnswer'}
                    label={<p className={classes.inputTitle}>Correct Final Human Answer</p>}
                >
                    <Input
                        className={classes.input}
                        placeholder="Enter the correct final human answer"
                    />
                </Form.Item>
                {props.hasPromptOptions && (
                    <Form.Item<GenerateResponseFormType>
                        className={classes.inputField}
                        rules={[
                            {
                                required: true,
                                message: 'Please select the actual correct option',
                            },
                        ]}
                        name={'humanFinalAnswer'}
                        label={<p className={classes.inputTitle}>Actual Correct option</p>}
                    >
                        <Select className={classes.input} placeholder="Select between A/B/C/D">
                            <Select.Option value={'A'}>A</Select.Option>
                            <Select.Option value={'B'}>B</Select.Option>
                            <Select.Option value={'C'}>C</Select.Option>
                            <Select.Option value={'D'}>D</Select.Option>
                        </Select>
                    </Form.Item>
                )}
                <div className={classes.fullWidth}>
                    <Form.Item<GenerateResponseFormType>
                        className={classes.inputField}
                        rules={[
                            {
                                required: true,
                                message: 'Please enter the explanation',
                            },
                        ]}
                        name={'humanFinalAnswerReasoning'}
                        label={
                            <p className={classes.inputTitle}>
                                Briefly explain how you got to your final human answer
                            </p>
                        }
                    >
                        <TextArea className={classes.input} rows={5} />
                    </Form.Item>
                </div>
                <div className={classes.annotationSeparator} />
                {props.hasPromptOptions && (
                    <Form.Item<GenerateResponseFormType>
                        className={classes.inputField}
                        rules={[
                            {
                                required: true,
                                message:
                                    'Please select from which response, reasoning trace was taken',
                            },
                        ]}
                        name={'modelReasoningTakenFrom'}
                        label={
                            <p className={classes.inputTitle}>
                                Model Response (from where Reasoning trace was taken)
                            </p>
                        }
                    >
                        <Select options={options} />
                    </Form.Item>
                )}
            </>
        )
    }, [props.enableTurnAnnotations, props.hasPromptOptions, options])

    const additionalFields = useMemo(() => {
        if (props.attributesUI) {
            return (
                <ModelFeedbackFormField
                    feedbackValues={feedbackValues}
                    modelResponseFeedbackCode={
                        props.modelResponseExtraInfo.modelResponseFeedbackCode
                    }
                />
            )
        }
        return (
            <>
                <Form.Item<GenerateResponseFormType>
                    className={classes.inputField}
                    rules={[
                        {
                            required: true,
                            message: 'Please select the option',
                        },
                    ]}
                    name="accuracy"
                    label={
                        <p className={classes.inputTitle}>
                            Was the model response and final answer correct?{' '}
                        </p>
                    }
                >
                    <Select className={classes.input} options={InAccurateReasonOptions} />
                </Form.Item>
                <Flex className={classes.fullWidth}>
                    <Form.Item<GenerateResponseFormType>
                        className={classes.inputField}
                        rules={[{ required: true, message: 'Domain is required' }]}
                        name="domain"
                        label={<p className={classes.inputTitle}>Domain</p>}
                    >
                        <Input className={classes.input} placeholder="Eg: biology" />
                    </Form.Item>
                    <Form.Item<GenerateResponseFormType>
                        className={classes.inputField}
                        name="subDomain"
                        label={<p className={classes.inputTitle}>Subdomain</p>}
                        rules={[{ required: true, message: 'Subdomain is required' }]}
                    >
                        <Input className={classes.input} placeholder="Eg: Neuroscience" />
                    </Form.Item>
                </Flex>
                <Form.Item<GenerateResponseFormType>
                    className={classes.inputField}
                    rules={[
                        {
                            min: 1,
                            required: true,
                            message: 'Please enter the time to develop strategy',
                            type: 'number',
                        },
                    ]}
                    name="timeToDevelopStrategy"
                    label={<p className={classes.inputTitle}>Time to develop strategy</p>}
                >
                    <InputNumber
                        controls={false}
                        className={classes.input}
                        placeholder="Enter time in mins"
                    />
                </Form.Item>
                <Form.Item<GenerateResponseFormType>
                    className={classes.inputField}
                    rules={[
                        {
                            required: true,
                            message: 'Please select the complexity level',
                        },
                    ]}
                    name="complexityLevel"
                    label={<p className={classes.inputTitle}>Complexity level</p>}
                >
                    <Select className={classes.input} options={ComplexityLevelOptions} />
                </Form.Item>
                <Form.Item<GenerateResponseFormType>
                    className={classes.inputField}
                    rules={[
                        {
                            min: 1,
                            required: true,
                            message: 'Please enter the time to solve',
                            type: 'number',
                        },
                    ]}
                    name="timeToSolve"
                    label={<p className={classes.inputTitle}>Time to solve</p>}
                >
                    <InputNumber
                        controls={false}
                        className={classes.input}
                        placeholder="Enter time in mins"
                    />
                </Form.Item>
                <Form.Item<GenerateResponseFormType>
                    className={classes.inputField}
                    rules={[{ required: true, message: 'Please select the difficulty level' }]}
                    name={'difficultyLevel'}
                    label={<p className={classes.inputTitle}>Difficulty level</p>}
                >
                    <Select className={classes.input}>
                        <Select.Option value={DifficultyLevel.Easy}>Easy</Select.Option>
                        <Select.Option value={DifficultyLevel.Medium}>Medium</Select.Option>
                        <Select.Option value={DifficultyLevel.Hard}>Hard</Select.Option>
                    </Select>
                </Form.Item>
                {fieldsForWithoutTurnAnnotationsFlow}
                <ModelFeedbackFormField
                    feedbackValues={feedbackValues}
                    modelResponseFeedbackCode={
                        props.modelResponseExtraInfo.modelResponseFeedbackCode
                    }
                />
            </>
        )
    }, [
        feedbackValues,
        fieldsForWithoutTurnAnnotationsFlow,
        props.attributesUI,
        props.modelResponseExtraInfo.modelResponseFeedbackCode,
    ])

    const liveFeedbackPlugin = useMemo(() => {
        return props.installedPlugins?.find(
            plugin => plugin.id === 'Live Feedback'
        ) as LiveFeedbackPlugin
    }, [props.installedPlugins])

    const domain = useMemo(() => {
        if (props.isLMFProject) return 'LMF'
        if (props.isStemProject) return 'Stem'
        return 'Math'
    }, [props.isLMFProject, props.isStemProject])

    return (
        <div className={classes.basicInfo}>
            <div className={classes['width-50']}>{props.attributesUI?.element ?? null}</div>
            <Form
                form={form}
                data-testid="generateResponseForm"
                layout="vertical"
                initialValues={props.initialValues ?? initialValues}
                className={classes.inputContainer}
                onFinish={() => {
                    setIsSubmitModalVisible(true)
                }}
                requiredMark={false}
                disabled={isReadOnly}
                onValuesChange={(
                    changedValues: Partial<GenerateResponseFormType>,
                    allValues: GenerateResponseFormType
                ) => {
                    if (changedValues.modelFeedbackDetails) {
                        changedValues.modelFeedbackDetails.forEach((item, index) => {
                            if (item?.feedbackUsefulness === 'complete') {
                                form.setFieldsValue({
                                    modelFeedbackDetails: allValues.modelFeedbackDetails?.map(
                                        (entry, idx) =>
                                            idx === index
                                                ? {
                                                      ...entry,
                                                      feedbackUsefulnessJustification: undefined,
                                                  }
                                                : entry
                                    ),
                                })
                            }
                        })
                    }
                }}
            >
                {additionalFields}
            </Form>

            <LiveFeedback
                isEnabled={!!liveFeedbackPlugin}
                modelResponse={props.modelResponseExtraInfo.modelResponse}
                humanAnswer={humanFinalAnswer}
                humanReasoningAnswer={humanReasoningAnswer}
                domain={domain}
                pluginId={liveFeedbackPlugin?.getConfig().id ?? ''}
                pluginConfig={
                    (liveFeedbackPlugin?.getConfig() as PluginConfigType<LiveFeedbackPluginConfigType>) ??
                    null
                }
                question={props.question}
                setIsLiveFeedbackChecked={props.setIsLiveFeedbackChecked}
                liveFeedbackContext={props.liveFeedbackContext}
                areAttributesValid={props.areAttributesValid}
            />

            {submitContents}
        </div>
    )
}
